import { useEffect, useRef, useState } from 'react';

// Charts animate on first load and again only after being idle for COOLDOWN_MS.
// Reloads within that window render statically. The cycle anchor is shared across
// every chart so a page's charts animate together as one batch, then stay calm.
const COOLDOWN_MS = 600000; // re-arm window: animate again only after this much idle (10m)
const BATCH_MS = 3000; // charts loading together in one refresh all animate
const DURATION_MS = 400;
const STORAGE_KEY = 'chartAnimCycleAnchor';

// Persisted so a hard browser reload within the cooldown also stays calm.
let cycleAnchorFallback = 0;

function readAnchor() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return cycleAnchorFallback;
  }
}

function writeAnchor(value) {
  cycleAnchorFallback = value;
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // localStorage unavailable (private mode / disabled) — fall back to memory
  }
}

function openAnimationCycle() {
  const now = Date.now();
  const anchor = readAnchor();
  if (now - anchor > COOLDOWN_MS) {
    writeAnchor(now); // start a fresh cycle
    return true;
  }
  return now - anchor < BATCH_MS; // same batch as a recent cycle
}

/**
 * Returns recharts animation props ({ isAnimationActive, animationDuration }) to
 * spread onto a chart series. Drive it with the chart's `loading` flag — the
 * chart animates when loading clears (data arrives), subject to the shared cooldown.
 */
export function useChartAnimation(loading = false) {
  const prevLoading = useRef(null);
  const activeUntil = useRef(0);
  const [, bump] = useState(0);

  const was = prevLoading.current;
  const loadEvent = was === null ? !loading : was && !loading;
  prevLoading.current = loading;

  if (loadEvent && openAnimationCycle()) {
    activeUntil.current = Date.now() + DURATION_MS + 150;
  }
  const isAnimationActive = Date.now() < activeUntil.current;

  // Flip the flag off once the entrance animation finishes so later re-renders
  // (e.g. inline-mapped data arrays) don't replay it.
  useEffect(() => {
    if (!isAnimationActive) return undefined;
    const timer = setTimeout(() => bump(n => n + 1), activeUntil.current - Date.now());
    return () => clearTimeout(timer);
  });

  return { isAnimationActive, animationDuration: DURATION_MS };
}
